import { lazy, Suspense, useContext, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ClinicAuthProvider, ClinicAuthContext } from "./auth/ClinicAuthContext";
import ClinicLogin from "./pages/ClinicLogin";
import api from "./api/client";
import { FeedbackProvider } from "./components/Feedback";
import { ChunkErrorBoundary, RouteFallback } from "./components/RouteLoading";
import AppLayout, { DOCTOR_NAV_ITEMS } from "./layout/AppLayout";

// Each page is its own downloadable chunk, so a phone loads only the screen it opens.
// The shell (AppLayout) and the sign-in page stay in the main bundle.
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const ConfirmEmailChange = lazy(() => import("./pages/ConfirmEmailChange"));
const ClinicSelect = lazy(() => import("./pages/ClinicSelect"));
const DoctorHome = lazy(() => import("./pages/DoctorHome"));
const DoctorPatientQueue = lazy(() => import("./pages/DoctorPatientQueue"));
const DoctorPatientsListPage = lazy(() => import("./pages/DoctorPatientsListPage"));
const DoctorSessionsPage = lazy(() => import("./pages/DoctorSessionsPage"));
const DoctorAppointmentsPage = lazy(() => import("./pages/DoctorAppointmentsPage"));
const DoctorProfilePage = lazy(() => import("./pages/DoctorProfilePage"));
const DoctorReportsPage = lazy(() => import("./pages/DoctorReportsPage"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminQueue = lazy(() => import("./pages/AdminQueue"));
const AdminDoctors = lazy(() => import("./pages/AdminDoctors"));
const DoctorSchedulePage = lazy(() => import("./pages/DoctorSchedulePage"));
const AdminAppointments = lazy(() => import("./pages/AdminAppointments"));
const AdminSettings = lazy(() => import("./pages/AdminSettings"));
const MyAccountPage = lazy(() => import("./pages/MyAccountPage"));
const SessionsPage = lazy(() => import("./pages/SessionsPage"));
const PatientsListPage = lazy(() => import("./pages/PatientsListPage"));
const PatientProfilePage = lazy(() => import("./pages/PatientProfilePage"));
const InvoicesPage = lazy(() => import("./pages/InvoicesPage"));
const AdminRefunds = lazy(() => import("./pages/AdminRefunds"));
const PrintableInvoice = lazy(() => import("./pages/PrintableInvoice"));
const PrintablePrescription = lazy(() => import("./pages/PrintablePrescription"));
const TreatmentCatalogPage = lazy(() => import("./pages/TreatmentCatalogPage"));
const ReportsPage = lazy(() => import("./pages/ReportsPage"));
const HRAdminPage = lazy(() => import("./pages/HRAdminPage"));
const RemindersPage = lazy(() => import("./pages/RemindersPage"));
const AttendanceKioskPage = lazy(() => import("./pages/AttendanceKioskPage"));

function RequireAuth({ children }) {
  const { token } = useContext(ClinicAuthContext);
  const location = useLocation();
  // Remember where they were headed so login can send them back there.
  return token ? children : <Navigate to="/login" replace state={{ from: location }} />;
}

// "/" and any unknown path: a signed-in user goes to their home, not the
// login form (a signed-out one still lands on /login).
function HomeRedirect() {
  const { token } = useContext(ClinicAuthContext);
  const [role, setRole] = useState(null);
  useEffect(() => {
    if (!token) return;
    api.get("my-account/").then((res) => setRole(res.data.role)).catch(() => setRole("none"));
  }, [token]);
  if (!token || role === "none") return <Navigate to="/login" replace />;
  if (!role) return null;
  return <Navigate to={role === "doctor" ? "/select-clinic" : "/admin"} replace />;
}

function RequireOrg({ children }) {
  const { orgId } = useContext(ClinicAuthContext);
  return orgId ? children : <Navigate to="/select-clinic" replace />;
}

export default function App() {
  return (
    <ClinicAuthProvider>
      <FeedbackProvider>
      <BrowserRouter>
        <ChunkErrorBoundary>
        <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/login" element={<ClinicLogin />} />
          <Route path="/reset-password/:uid/:token" element={<ResetPassword />} />
          <Route path="/confirm-email-change/:token" element={<ConfirmEmailChange />} />

          <Route
            path="/select-clinic"
            element={
              <RequireAuth>
                <ClinicSelect />
              </RequireAuth>
            }
          />

          {/* Doctor-facing area — org context comes from the switcher
              (RequireOrg), unlike the staff /admin area below where it's
              already fixed server-side. */}
          <Route
            path="/doctor"
            element={
              <RequireAuth>
                <RequireOrg>
                  <AppLayout navItems={DOCTOR_NAV_ITEMS} fetchBranding={false} showSwitchOrg />
                </RequireOrg>
              </RequireAuth>
            }
          >
            <Route index element={<DoctorHome />} />
            <Route path="sessions" element={<DoctorSessionsPage />} />
            <Route path="appointments" element={<DoctorAppointmentsPage />} />
            <Route path="patients" element={<DoctorPatientsListPage />} />
            <Route path="patients/:patientId" element={<DoctorPatientQueue />} />
            <Route path="reports" element={<DoctorReportsPage />} />
            <Route path="profile" element={<DoctorProfilePage />} />
          </Route>

          <Route path="/dashboard" element={<Navigate to="/doctor" replace />} />

          {/* Clinic STAFF (Employee) admin area — nested under the
              responsive frame. No RequireOrg here: staff org context is
              already fixed server-side, unlike the doctor flow above. */}
          <Route
            path="/admin"
            element={
              <RequireAuth>
                <AppLayout />
              </RequireAuth>
            }
          >
            <Route index element={<AdminDashboard />} />
            <Route path="queue" element={<AdminQueue />} />
            <Route path="doctors" element={<AdminDoctors />} />
            <Route path="doctors/:affiliationId/schedule" element={<DoctorSchedulePage />} />
            <Route path="appointments" element={<AdminAppointments />} />
            <Route path="sessions" element={<SessionsPage />} />
            <Route path="patients" element={<PatientsListPage />} />
            <Route path="patients/:patientId" element={<PatientProfilePage />} />
            <Route path="invoices" element={<InvoicesPage />} />
            <Route path="refunds" element={<AdminRefunds />} />
            <Route path="reports" element={<ReportsPage />} />
            <Route path="treatment-catalog" element={<TreatmentCatalogPage />} />
            <Route path="hr-admin" element={<HRAdminPage />} />
            <Route path="reminders" element={<RemindersPage />} />
            <Route path="attendance-kiosk" element={<AttendanceKioskPage />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="my-account" element={<MyAccountPage />} />
          </Route>

          {/* Deliberately OUTSIDE AppLayout — a printed invoice should
              never carry the sidebar/topbar chrome, even before print
              CSS kicks in (e.g. someone opens this link directly). */}
          <Route
            path="/admin/invoices/:invoiceId/print"
            element={
              <RequireAuth>
                <PrintableInvoice />
              </RequireAuth>
            }
          />

          {/* Same reasoning as the invoice print route above — a doctor
              printing a prescription for the patient to walk out with
              shouldn't see the app chrome either. Mounted for both roles
              (doctor and staff) since either can print — apiBase picks
              the matching role-scoped endpoint (see PrintablePrescription
              and clinic/views_admin.prescription_detail_staff). */}
          <Route
            path="/doctor/prescriptions/:prescriptionId/print"
            element={
              <RequireAuth>
                <PrintablePrescription />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/prescriptions/:prescriptionId/print"
            element={
              <RequireAuth>
                <PrintablePrescription apiBase="admin/prescriptions" />
              </RequireAuth>
            }
          />

          <Route path="*" element={<HomeRedirect />} />
        </Routes>
        </Suspense>
        </ChunkErrorBoundary>
      </BrowserRouter>
      </FeedbackProvider>
    </ClinicAuthProvider>
  );
}